import { sum } from 'lodash';
import pluralize from 'pluralize';
import { useMemo } from 'react';
import { FileRejection } from 'react-dropzone';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { IconTrash } from '@u21/tabler-icons';

import { downloadAttachmentThunk } from 'app/modules/attachments/sliceAttachments';
import {
  ATTACHMENT_ACTIONS_COLUMN,
  ATTACHMENT_COLUMNS,
} from 'app/modules/goAML/columns';
import {
  ACCEPTABLE_FILE_EXTENSIONS,
  DOCUMENT_UPLOAD_LIMIT,
} from 'app/modules/goAML/constants';
import { useGetGoamlFiling } from 'app/modules/goAML/queries/useGetGoamlFiling';
import {
  useAddGoamlAttachments,
  useDeleteGoamlAttachment,
} from 'app/modules/goAML/queries/useGoamlAttachments';
import { useFlattenedFormErrors } from 'app/modules/filingFormGenerator/contexts/flattenedErrorContext';
import { FilingFormGeneratorPageParams } from 'app/modules/filingFormGenerator/models';
import { selectHasEditSARsPermission } from 'app/modules/session/selectors';
import { humanReadableFileSize } from 'app/shared/helpers';
import { sendToast } from 'app/shared/toasts/actions';
import {
  U21Alert,
  U21Chip,
  U21FileUploader,
  U21MenuItemProps,
  U21Section,
  U21Spacer,
  U21Table,
} from 'app/shared/u21-ui/components';

export const GoamlAttachmentsTab = () => {
  const dispatch = useDispatch();
  const { id } = useParams<FilingFormGeneratorPageParams>();
  const filingId = Number.parseInt(id, 10);
  const canEditGoAMLSTR = useSelector(selectHasEditSARsPermission);
  const { data: filing } = useGetGoamlFiling(filingId);
  const attachments = filing?.attachments ?? [];
  const { mutate: addAttachments, isPending: isAdding } =
    useAddGoamlAttachments(filingId);
  const { mutate: deleteAttachment } = useDeleteGoamlAttachment(filingId);
  const { flattenedErrors } = useFlattenedFormErrors();
  const attachmentError =
    typeof flattenedErrors.associated_files === 'string'
      ? flattenedErrors.associated_files
      : undefined;

  const totalBytes = sum(attachments.map((item) => item.file_size || 0)) || 0;
  const totalBytesLabel = totalBytes
    ? `( ${humanReadableFileSize(totalBytes)} / ${humanReadableFileSize(
        DOCUMENT_UPLOAD_LIMIT,
      )} )`
    : '';
  const headerLabel = attachments.length
    ? `${pluralize('attachment', attachments.length, true)} ${totalBytesLabel}`
    : 'No attachments';

  const onDrop = (acceptedFiles: File[], rejectedFiles: FileRejection[]) => {
    if (!acceptedFiles.length) {
      dispatch(
        sendToast({
          message: 'No accepted files chosen to upload.',
          type: 'Error',
          position: 'Top',
        }),
      );
      return;
    }
    const batchBytes = sum(acceptedFiles.map((file) => file.size));
    if (totalBytes + batchBytes > DOCUMENT_UPLOAD_LIMIT) {
      dispatch(
        sendToast({
          message: `File upload limit of ${humanReadableFileSize(
            DOCUMENT_UPLOAD_LIMIT,
          )} exceeded! Please upload fewer or smaller files.`,
          type: 'Error',
          position: 'Top',
        }),
      );
      return;
    }

    addAttachments({ id: filingId, files: acceptedFiles });

    if (rejectedFiles.length > 0) {
      const rejectedNames = new Intl.ListFormat('en-US').format(
        rejectedFiles.map((rejection) => rejection.file.name),
      );
      dispatch(
        sendToast({
          message: `Error uploading the following file(s): ${rejectedNames}`,
          type: 'Error',
          position: 'Top',
        }),
      );
    }
  };

  const columns = useMemo(
    () => [
      ...ATTACHMENT_COLUMNS,
      {
        ...ATTACHMENT_ACTIONS_COLUMN,
        cellProps: (row) => {
          const items: U21MenuItemProps[] = [
            {
              text: 'Download',
              onClick: () => {
                dispatch(
                  downloadAttachmentThunk({
                    id: row.id,
                    fileName: row.name,
                  }),
                );
              },
            },
          ];
          if (canEditGoAMLSTR) {
            items.push({
              color: 'error',
              icon: <IconTrash />,
              onClick: () => deleteAttachment(row.id),
              text: 'Delete',
            });
          }
          return { items };
        },
      },
    ],
    [canEditGoAMLSTR, deleteAttachment, dispatch],
  );

  return (
    <U21Section
      disableTitleCase
      title={
        <StyledHeader>
          <span>Attachments</span>
          <U21Chip>{headerLabel}</U21Chip>
        </StyledHeader>
      }
    >
      <U21Spacer spacing={2}>
        {attachmentError ? (
          <U21Alert color="error">{attachmentError}</U21Alert>
        ) : null}
        <U21Spacer spacing={5}>
          {canEditGoAMLSTR && (
            <U21FileUploader
              loading={isAdding}
              accept={ACCEPTABLE_FILE_EXTENSIONS}
              maxSize={DOCUMENT_UPLOAD_LIMIT - totalBytes}
              onDrop={onDrop}
            />
          )}
          <U21Table
            columns={columns}
            data={attachments}
            noDataComponent={null}
          />
        </U21Spacer>
      </U21Spacer>
    </U21Section>
  );
};

const StyledHeader = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 0.5rem;
`;
